import { createProvider } from '@nvision/services/data-provider';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import type { ProjectsSearchParams } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';
import * as api from './api';
import * as mock from './mock';

export const fetchProjects = createProvider(api.fetchProjects)
  .andMock(mock.fetchProjects)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch projects is aborted');
    } else {
      logger.error(`Fetch projects failed: ${error}`);
    }
    throw error;
  });

export const PROJECTS_PAGE_SIZE = 10;

export const projectsInfiniteQueryOptions = (filters: ProjectsSearchParams) =>
  // biome-ignore assist/source/useSortedKeys: TODO: wait for tanstack query update
  infiniteQueryOptions({
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      fetchProjects({ ...filters, skip: pageParam, take: PROJECTS_PAGE_SIZE }, { signal }),
    queryKey: CACHE_KEYS.projects(filters),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((count, page) => count + page.items.length, 0);

      return loadedCount < lastPage.totalCount ? loadedCount : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items),
      totalCount: data.pages[0]?.totalCount ?? 0,
    }),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchSortingOptions = createProvider(api.fetchSortingOptions).andThen((res) => res);

export const sortingOptionsQueryOptions = () =>
  queryOptions({
    queryFn: ({ signal }) => fetchSortingOptions({ signal }),
    queryKey: CACHE_KEYS.sortingOptions(),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });
